/**
 * FeedbackModal Component
 * Detailed feedback form with issue categorization and comments
 * 
 * Sprint 1, Task 11: User Feedback Collection UI
 */

import React, { useState } from 'react';
import { X, AlertCircle, FileText, Target, CheckCircle } from 'lucide-react';
import { useFeedbackStore } from '../../stores/feedbackStore';

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryId: string;
  projectId: string;
  answer: string;
  citations?: any[];
  initialFeedbackType?: 'helpful' | 'not_helpful';
}

const ISSUE_CATEGORIES = [
  { value: 'hallucination', label: 'Incorrect/Hallucinated Information', icon: AlertCircle },
  { value: 'outdated', label: 'Outdated Information', icon: FileText },
  { value: 'incomplete', label: 'Incomplete Answer', icon: Target },
  { value: 'wrong_source', label: 'Wrong or Irrelevant Sources', icon: FileText },
  { value: 'formatting', label: 'Poor Formatting', icon: FileText },
  { value: 'other', label: 'Other Issue', icon: AlertCircle }
];

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  queryId,
  projectId,
  answer,
  citations = [],
  initialFeedbackType
}) => {
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState<number | null>(initialFeedbackType === 'helpful' ? 5 : null);
  const [issueCategory, setIssueCategory] = useState<string>('');
  const [comment, setComment] = useState('');
  const [relevanceScore, setRelevanceScore] = useState<number>(3);
  const [accuracyScore, setAccuracyScore] = useState<number>(3);
  const [completenessScore, setCompletenessScore] = useState<number>(3);
  const [citationQualityScore, setCitationQualityScore] = useState<number>(3);
  const [submitted, setSubmitted] = useState(false);

  const { submitFeedback, isSubmitting } = useFeedbackStore();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const feedback = {
      query_id: queryId,
      project_id: projectId,
      feedback_type: rating && rating >= 4 ? 'helpful' : 'not_helpful',
      rating,
      issue_category: issueCategory || undefined,
      comment: comment.trim() || undefined,
      answer_relevance_score: relevanceScore,
      accuracy_score: accuracyScore,
      completeness_score: completenessScore,
      citation_quality_score: citationQualityScore,
      timestamp: new Date().toISOString()
    };

    try {
      await submitFeedback(feedback);
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setStep(1);
        setRating(null);
        setIssueCategory('');
        setComment('');
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit detailed feedback:', error);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Rate this answer</h3>
        <p className="text-sm text-gray-600 mb-4">How would you rate the overall quality?</p>
        
        <div className="flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              className={`w-12 h-12 rounded-full border-2 transition-all ${
                rating && rating >= star
                  ? 'border-yellow-500 bg-yellow-50 text-yellow-600'
                  : 'border-gray-300 text-gray-400 hover:border-yellow-300'
              }`}
            >
              <span className="text-xl">★</span>
            </button>
          ))}
        </div>
        
        <div className="flex justify-between text-xs text-gray-500 mt-2 px-2">
          <span>Poor</span>
          <span>Excellent</span>
        </div>
      </div>

      {rating && rating < 4 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">What was the issue?</h4>
          <div className="grid grid-cols-2 gap-2">
            {ISSUE_CATEGORIES.map((category) => (
              <button
                key={category.value}
                onClick={() => setIssueCategory(category.value)}
                className={`flex items-center gap-2 p-3 text-sm border rounded-lg transition-colors ${
                  issueCategory === category.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <category.icon className="w-4 h-4" />
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => setStep(2)}
          disabled={!rating}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Assessment</h3>
        
        <div className="space-y-4">
          <div>
            <label className="flex justify-between text-sm font-medium text-gray-700 mb-2">
              <span>Relevance to Question</span>
              <span className="text-blue-600">{relevanceScore}/5</span>
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={relevanceScore}
              onChange={(e) => setRelevanceScore(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          <div>
            <label className="flex justify-between text-sm font-medium text-gray-700 mb-2">
              <span>Accuracy of Information</span>
              <span className="text-blue-600">{accuracyScore}/5</span>
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={accuracyScore}
              onChange={(e) => setAccuracyScore(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          <div>
            <label className="flex justify-between text-sm font-medium text-gray-700 mb-2">
              <span>Completeness</span>
              <span className="text-blue-600">{completenessScore}/5</span>
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={completenessScore}
              onChange={(e) => setCompletenessScore(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          {citations && citations.length > 0 && (
            <div>
              <label className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                <span>Quality of Citations</span>
                <span className="text-blue-600">{citationQualityScore}/5</span>
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={citationQualityScore}
                onChange={(e) => setCitationQualityScore(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Comments (Optional)
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tell us more about your experience..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          Your feedback helps us improve the system for everyone.
        </p>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank You!</h3>
      <p className="text-sm text-gray-600">
        Your feedback has been submitted successfully.
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {submitted ? 'Feedback Submitted' : 'Provide Feedback'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-6">
          {submitted ? renderSuccess() : step === 1 ? renderStep1() : renderStep2()}
        </div>

        {!submitted && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex gap-2">
              <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;

